const getApiBaseUrl = () => {
  if (process.env.NODE_ENV === "development") {
    // In React development, an empty string ensures all fetch() requests to /api/* 
    // are transparently passed down to the proxy (http://localhost:5000) defined in package.json
    // This allows it to magically work on localhost AND via Ngrok transparently.
    return "";
  }

  // In production, use same origin as frontend
  return window.location.origin;
};

export const API_BASE_URL = getApiBaseUrl();

// API endpoints
export const API_ENDPOINTS = {
  // Base URL for dynamic endpoints
  BASE_URL: API_BASE_URL,

  // Auth endpoints
  LOGIN: `${API_BASE_URL}/api/auth/login`,
  GET_CAPTCHA: `${API_BASE_URL}/api/auth/captcha`,
  LOGOUT: `${API_BASE_URL}/api/auth/logout`,
  BROWSER_CLOSE: `${API_BASE_URL}/api/auth/browser-close`,
  REGISTER: `${API_BASE_URL}/api/auth/register`,
  VERIFY_ADMIN: `${API_BASE_URL}/api/auth/admin/verify`,
  FORGOT_PASSWORD: `${API_BASE_URL}/api/auth/forgot-password`,
  RESET_PASSWORD: `${API_BASE_URL}/api/auth/reset-password`,
  VALIDATE_USERNAME: `${API_BASE_URL}/api/auth/validate-username`,
  VERIFY_TOKEN: `${API_BASE_URL}/api/auth/verify-token`,
  VERIFY_RESET_TOKEN: `${API_BASE_URL}/api/auth/verify-reset-token`,
  AUTH_HEALTH: `${API_BASE_URL}/api/auth/health`,

  // User endpoints
  USERS: `${API_BASE_URL}/api/auth/admin/users`,
  CREATE_USER: `${API_BASE_URL}/api/auth/admin/create-user`,
  UPDATE_USER_ROLE: `${API_BASE_URL}/api/auth/admin/update-role`,
  UPDATE_USERNAME: `${API_BASE_URL}/api/auth/admin/update-username`,
  UPDATE_USER_NAME: `${API_BASE_URL}/api/auth/admin/update-name`,
  UPDATE_PASSWORD: `${API_BASE_URL}/api/auth/admin/update-password`,
  DELETE_USER: `${API_BASE_URL}/api/auth/admin/delete-user`,
  PASSWORD_HISTORY: `${API_BASE_URL}/api/auth/admin/password-history`,
  DELETE_PASSWORD_HISTORY: `${API_BASE_URL}/api/auth/admin/delete-password-history`,

  // Revenue endpoints
  REVENUE: `${API_BASE_URL}/api/revenue`,
  REVENUE_SUMMARY: `${API_BASE_URL}/api/revenue/summary`,
  REVENUE_OCCUPANCY: `${API_BASE_URL}/api/revenue/occupancy`,
  REVENUE_REFRESH: `${API_BASE_URL}/api/revenue/refresh`,
  REVENUE_CACHE_STATS: `${API_BASE_URL}/api/revenue/cache-stats`,
  REVENUE_CLEAR_CACHE: `${API_BASE_URL}/api/revenue/clear-cache`,
  REVENUE_WARM_CACHE: `${API_BASE_URL}/api/revenue/warm-cache`,
  REVENUE_HEALTH: `${API_BASE_URL}/api/revenue/health`,
  REVENUE_REFRESH_LISTINGS: `${API_BASE_URL}/api/revenue/refresh-listings`,
  REVENUE_LISTINGS: `${API_BASE_URL}/api/revenue/listings`,
  REVENUE_CLEAR_LISTINGS_CACHE: `${API_BASE_URL}/api/revenue/clear-listings-cache`,
  REVENUE_CRON: `${API_BASE_URL}/api/revenue/cron`,
  REVENUE_TEST_MONTHLY_TARGET: `${API_BASE_URL}/api/revenue/test-monthly-target`,

  // Revenue Table endpoints (Ultra Fast Database)
  REVENUE_TABLE_FAST_DASHBOARD: `${API_BASE_URL}/api/revenue-table/fast-dashboard-data`,
  REVENUE_TABLE_DATA: `${API_BASE_URL}/api/revenue-table/revenue-data`,
  REVENUE_TABLE_LISTING_DATA: `${API_BASE_URL}/api/revenue-table/listing-revenue-data`,

  // Teable endpoints
  TEABLE_STATUS: `${API_BASE_URL}/api/teable/status`,
  TEABLE_START: `${API_BASE_URL}/api/teable/start`,
  TEABLE_STOP: `${API_BASE_URL}/api/teable/stop`,
  TEABLE_RESET_COOLDOWN: `${API_BASE_URL}/api/teable/reset-cooldown`,
  TEABLE_POSTING_STATUS: `${API_BASE_URL}/api/teable/posting-status`,
  TEABLE_TEST_CONNECTION: `${API_BASE_URL}/api/teable/test-connection`,
  TEABLE_MANUAL_POST: `${API_BASE_URL}/api/teable/manual-post`,
  TEABLE_POST_SPECIFIC: `${API_BASE_URL}/api/teable/post-specific`,
  TEABLE_DEBUG_HOUR_CHECK: `${API_BASE_URL}/api/teable/debug-hour-check`,

  // Monthly target
  MONTHLY_TARGET: `${API_BASE_URL}/api/monthly-target`,
  QUARTERLY_TARGET: `${API_BASE_URL}/api/quarterly-achieved`,

  // Occupancy endpoints
  OCCUPANCY: `${API_BASE_URL}/api/occupancy`,
  OCCUPANCY_CURRENT: `${API_BASE_URL}/api/occupancy/current`,
  OCCUPANCY_HEALTH: `${API_BASE_URL}/api/occupancy/health`,
  OCCUPANCY_REPORT: `${API_BASE_URL}/api/occupancy/report`,
  OCCUPANCY_TODAY_CHECKINS: `${API_BASE_URL}/api/occupancy/today-checkins`,
  OCCUPANCY_SYNC_LISTINGS: `${API_BASE_URL}/api/occupancy/sync-listings`,

  // Rooms endpoints
  ROOMS_LISTINGS: `${API_BASE_URL}/api/rooms/listings`,
  ROOMS_LISTING_BY_ID: `${API_BASE_URL}/api/rooms/listings`, // + /:id
  ROOMS_CLEANING_STATUS: `${API_BASE_URL}/api/rooms/cleaning-status`,
  ROOMS_UPDATE_CLEANING_STATUS_OLD: `${API_BASE_URL}/api/rooms/cleaning-status`, // + /:id (PUT)
  ROOMS_UPDATE_CLEANING_STATUS: `${API_BASE_URL}/api/rooms/update-cleaning-status`,
  ROOMS_AVAILABILITY: `${API_BASE_URL}/api/rooms/availability`,
  ROOMS_HEALTH: `${API_BASE_URL}/api/rooms/health`,
  ROOMS_TEST_CORS: `${API_BASE_URL}/api/rooms/test-cors`,
  ROOMS_TEST_TEABLE: `${API_BASE_URL}/api/rooms/test-teable`,

  // Rooms Teable endpoints
  ROOMS_TEABLE_SYNC: `${API_BASE_URL}/api/rooms-teable/sync`,
  ROOMS_TEABLE_DATA: `${API_BASE_URL}/api/rooms-teable/data`,
  ROOMS_TEABLE_TEST: `${API_BASE_URL}/api/rooms-teable/test`,

  // Room Availability Teable endpoints
  ROOM_AVAILABILITY_TEABLE_SYNC: `${API_BASE_URL}/api/room-availability-teable/sync`,
  ROOM_AVAILABILITY_TEABLE_DATA: `${API_BASE_URL}/api/room-availability-teable/data`,
  ROOM_AVAILABILITY_TEABLE_TEST: `${API_BASE_URL}/api/room-availability-teable/test`,

  // Room Details Teable endpoints
  ROOM_DETAILS_TEABLE_SYNC: `${API_BASE_URL}/api/room-details-teable/sync`,
  ROOM_DETAILS_TEABLE_DATA: `${API_BASE_URL}/api/room-details-teable/data`,
  ROOM_DETAILS_TEABLE_TEST: `${API_BASE_URL}/api/room-details-teable/test`,

  // Payment endpoints
  PAYMENT_TODAY_RESERVATIONS: `${API_BASE_URL}/api/payment/today-reservations`,
  PAYMENT_RESERVATION_BY_ID: `${API_BASE_URL}/api/payment/reservation`, // + /:id
  PAYMENT_HEALTH: `${API_BASE_URL}/api/payment/health`,

  // Payment Teable endpoints (fast loading with cache)
  PAYMENT_TEABLE_FAST: `${API_BASE_URL}/api/payment-teable/fast`,
  PAYMENT_TEABLE_SYNC: `${API_BASE_URL}/api/payment-teable/sync`,
  PAYMENT_TEABLE_RECORDS: `${API_BASE_URL}/api/payment-teable/records`,


  // Payment helper functions
  PAYMENT_GET_RESERVATION: (id) => `${API_BASE_URL}/api/payment/reservation/${id}`,
  PAYMENT_DETAILS: (id) => `${API_BASE_URL}/api/payment-details/${id}`,

  // Passcode Access endpoints
  PASSCODE_ACCESS_GET_DATA: (reservationId) => `${API_BASE_URL}/api/passcode-access/${reservationId}`,
  PASSCODE_ACCESS_VERIFY: `${API_BASE_URL}/api/passcode-access/verify`,

  // FDO Panel endpoints
  FDO_RESERVATIONS: `${API_BASE_URL}/api/fdo/reservations`,
  FDO_COMBINED_DATA: `${API_BASE_URL}/api/fdo/combined-data`,
  FDO_STREAM: `${API_BASE_URL}/api/fdo/stream`,
  FDO_COMMENTS: `${API_BASE_URL}/api/fdo/comments`,
  FDO_HOTELEYE: `${API_BASE_URL}/api/fdo/hoteleye`,
  FDO_NOTIFICATIONS: `${API_BASE_URL}/api/fdo/notifications`,
  FDO_SYNC_ALL: `${API_BASE_URL}/api/fdo/reservations/sync`,
  FDO_SYNC_SINGLE: (id) => `${API_BASE_URL}/api/fdo/reservations/${id}/sync`,
  FDO_CHECKIN: (id) => `${API_BASE_URL}/api/fdo/reservations/${id}/checkin`,
  FDO_CHECKOUT: (id) => `${API_BASE_URL}/api/fdo/reservations/${id}/checkout`,

  // Upsells endpoints
  UPSELLS: `${API_BASE_URL}/api/upsells`,
  UPSELLS_TEABLE_RECORDS: `${API_BASE_URL}/api/upsells/teable-records`,
  UPSELLS_CANCELLED: `${API_BASE_URL}/api/upsells/cancelled-reservations`,

  // Charges endpoints
  CHARGES: `${API_BASE_URL}/api/charges`,
  CHARGES_TEABLE_RECORDS: `${API_BASE_URL}/api/charges/teable-records`,

  // Cleaning endpoints
  CLEANING: `${API_BASE_URL}/api/cleaning`,

  // Security Deposit endpoints
  SECURITY_DEPOSIT: `${API_BASE_URL}/api/security-deposit`,
  SECURITY_DEPOSIT_TEABLE: `${API_BASE_URL}/api/security-deposit-teable`,
  SECURITY_DEPOSIT_SEARCH: `${API_BASE_URL}/api/security-deposit-search`,

  // Booking Summary endpoints
  BOOKING_SUMMARY_STATUS: `${API_BASE_URL}/api/booking-summary/status`,
  BOOKING_SUMMARY_WEBHOOK: `${API_BASE_URL}/api/booking-summary/webhook`,
  BOOKING_SUMMARY_UPDATES: `${API_BASE_URL}/api/booking-summary/updates`,
  BOOKING_SUMMARY_SEARCH: (phone) => `${API_BASE_URL}/api/booking-summary/search/${phone}`,

  // Portal URLs
  PORTAL_AUTH_URL: process.env.REACT_APP_PORTAL_AUTH_URL || 'https://portal.namuve.com/authentication/sign-in',
};

export default API_ENDPOINTS;
